/**
 * Motorcycles Dashboard
 * =====================
 * Overview of all motorcycles with grid display.
 */

import { Link } from 'react-router-dom'
import { Plus, Bike } from 'lucide-react'
import { useMotorcycles } from '@/hooks/useMotorcycles'
import { BIKE_STATUS_COLORS, BIKE_STATUS_LABELS } from '@/types'

export default function MotorcyclesDashboard() {
  const { data: motorcycles, isLoading, error } = useMotorcycles()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Failed to load motorcycles</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Motorcycles</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">
            {motorcycles?.length || 0} motorcycle{motorcycles?.length !== 1 ? 's' : ''} in collection
          </p>
        </div>
        <Link
          to="/motorcycles/new"
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          Add Motorcycle
        </Link>
      </div>

      {/* Motorcycles Grid */}
      {motorcycles && motorcycles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {motorcycles.map((bike) => (
            <Link
              key={bike.id}
              to={`/motorcycles/${bike.motorcycle_id}/information`}
              className="block bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow overflow-hidden"
            >
              {/* Image */}
              <div className="aspect-video bg-gray-100 dark:bg-gray-700 relative">
                {bike.image_url ? (
                  <img
                    src={bike.image_url}
                    alt={bike.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Bike size={48} className="text-gray-300 dark:text-gray-600" />
                  </div>
                )}
                {/* Status badge */}
                <span
                  className={`absolute top-2 right-2 px-2 py-1 text-xs font-medium rounded-full ${
                    BIKE_STATUS_COLORS[bike.status] || BIKE_STATUS_COLORS.running
                  }`}
                >
                  {BIKE_STATUS_LABELS[bike.status] || bike.status}
                </span>
              </div>

              {/* Info */}
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {bike.name}
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {bike.year} {bike.make} {bike.model}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                  {bike.motorcycle_id}
                </p>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-lg">
          <Bike size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No motorcycles yet
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Add your first motorcycle to get started
          </p>
          <Link
            to="/motorcycles/new"
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={20} />
            Add Motorcycle
          </Link>
        </div>
      )}
    </div>
  )
}
